import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import { svgIconClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { ButtonProps } from '@mui/material/Button';
import IconButton, { iconButtonClasses, IconButtonProps } from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DateRange, StaticDatePicker } from '@mui/x-date-pickers-pro';
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar';
import dayjs, { Dayjs } from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);

export type NullableDate = Dayjs | null;

enum DateRangePickerVariant {
  DATE_RANGE = 'date_range',
  SINGLE_DATE = 'single_date',
}

enum DateRangePickerMode {
  TEXT = 'text',
  ICON = 'icon',
}

const DATE_FORMAT_WITHOUT_YEAR = 'ddd MMM-DD';
const DATE_FORMAT_WITH_YEAR = 'ddd MMM-DD ‘YY';
const USE_TODAY_DATE_FORMAT = 'ddd MMM-DD-YYYY';

const DATE_RANGE_CONNECTOR = ' - ';

type DateRangePickerShortcut = {
  getValue: () => DateRange<Dayjs>;
  key: string;
  label: string;
};

export type DateRangePickerDateSelectFunction = (
  startDate: NullableDate,
  endDate: NullableDate,
  selectedShortcut?: string,
) => void;

const dateRangeVariantShortcuts: DateRangePickerShortcut[] = [
  {
    key: 'today',
    label: t('date.today'),
    getValue: () => {
      const today = dayjs.tz();
      return [today.startOf('day'), today.endOf('day')];
    },
  },
  {
    key: 'tomorrow',
    label: t('date.tomorrow'),
    getValue: () => {
      const today = dayjs.tz().add(1, 'days');
      return [today.startOf('day'), today.endOf('day')];
    },
  },
  {
    key: 'this_week',
    label: t('date.this_week'),
    getValue: () => {
      const today = dayjs.tz();
      return [today.weekday(0).startOf('day'), today.weekday(6).endOf('day')];
    },
  },
];

const singleDateVariantShortcuts: DateRangePickerShortcut[] = [
  {
    key: 'today',
    label: t('date.today'),
    getValue: () => [dayjs.tz(), null],
  },
  {
    key: 'tomorrow',
    label: t('date.tomorrow'),
    getValue: () => [dayjs.tz().add(1, 'days'), null],
  },
];

const getDateFormat = (date: Dayjs, useTodayFormat: boolean = false) => {
  if (useTodayFormat) {
    return USE_TODAY_DATE_FORMAT;
  }

  const isDateIsWithinCurrentYear = dayjs.tz().year() === date.year();

  return isDateIsWithinCurrentYear ? DATE_FORMAT_WITHOUT_YEAR : DATE_FORMAT_WITH_YEAR;
};

const getDateRangeVariantDisplayDate = (
  startDate: NullableDate,
  endDate: NullableDate,
  useTodayFormat: boolean = false,
) => {
  if (!startDate && !endDate) {
    return t('dispatch.dispatch_v2.filters.all_dates');
  }

  if (!startDate) {
    const date = endDate as Dayjs;
    const format = getDateFormat(date);

    return date.format(format);
  }

  const start = startDate as Dayjs;
  const startFormat = getDateFormat(start, useTodayFormat);
  let formattedStartDate = start.format(startFormat);

  if (!endDate) {
    return formattedStartDate;
  }

  const end = endDate as Dayjs;
  const endFormat = getDateFormat(end, useTodayFormat);
  let formattedEndDate = end.format(endFormat);

  let singleDateLabel = startDate.isToday() ? t('date.today') : formattedStartDate;

  if (useTodayFormat) {
    if (startDate.isToday()) {
      singleDateLabel = `${t('date.today')}, ${formattedStartDate}`;
    } else if (startDate.isTomorrow()) {
      singleDateLabel = `${t('date.tomorrow')}, ${formattedStartDate}`;
    }
  }

  if (formattedStartDate !== formattedEndDate) {
    if (useTodayFormat) {
      if (start.isToday()) {
        formattedStartDate = `${t('date.today')}, ${formattedStartDate}`;
      } else if (start.isTomorrow()) {
        formattedStartDate = `${t('date.tomorrow')}, ${formattedStartDate}`;
      }

      if (end.isToday()) {
        formattedEndDate = `${t('date.today')}, ${formattedEndDate}`;
      } else if (end.isTomorrow()) {
        formattedEndDate = `${t('date.tomorrow')}, ${formattedEndDate}`;
      }
    }

    return `${formattedStartDate}${DATE_RANGE_CONNECTOR}${formattedEndDate}`;
  }

  return singleDateLabel;
};

const getSingleDateVariantDisplayDate = (
  date: NullableDate,
  useTodayFormat: boolean = false,
) => {
  if (!date) {
    return t('dispatch.dispatch_v2.filters.all_dates');
  }

  const singleDate = date as Dayjs;
  const format = getDateFormat(singleDate, useTodayFormat);
  const formattedSingleDate = singleDate.format(format);

  if (useTodayFormat) {
    if (singleDate.isToday()) {
      return `${t('date.today')}, ${formattedSingleDate}`;
    }

    if (singleDate.isTomorrow()) {
      return `${t('date.tomorrow')}, ${formattedSingleDate}`;
    }

    return formattedSingleDate;
  }

  return singleDate.isToday() ? t('date.today') : formattedSingleDate;
};

const getDateRangePickerDisplayDate = (
  startDate: NullableDate,
  endDate: NullableDate,
  isDateRangeVariant: boolean,
  useTodayFormat: boolean = false,
) => {
  if (isDateRangeVariant) {
    return getDateRangeVariantDisplayDate(startDate, endDate, useTodayFormat);
  }

  return getSingleDateVariantDisplayDate(startDate, useTodayFormat);
};

export const getDateRangePickerIconModeDisplayDate = (
  startDate: NullableDate,
  endDate: NullableDate,
  isDateRangeVariant: boolean,
  useTodayFormat: boolean = false,
) => {
  const displayDate = getDateRangePickerDisplayDate(
    startDate,
    endDate,
    isDateRangeVariant,
    useTodayFormat,
  );
  const displayDateFragments = displayDate.split(DATE_RANGE_CONNECTOR);

  if (displayDateFragments.length > 1) {
    return t('common.range', {
      start: displayDateFragments[0],
      end: displayDateFragments[1],
    });
  }

  return displayDate;
};

const throwIfCustomShortcutsKeysAreNotSafe = (
  defaultShortcuts: DateRangePickerShortcut[],
  customShortcuts?: DateRangePickerShortcut[],
) => {
  const alreadyTakenShortcutKey = customShortcuts?.find((customShortcut) => {
    return defaultShortcuts.find((shortcut) => shortcut.key === customShortcut.key);
  })?.key;

  if (alreadyTakenShortcutKey) {
    throw new Error(
      `DateRangePicker error: custom shortcut key "${alreadyTakenShortcutKey}" has already been taken.`,
    );
  }
};

export type DateRangePickerProps = {
  allowAllDates?: boolean;
  customShortcuts?: DateRangePickerShortcut[];
  endDate: NullableDate;
  mode?: `${DateRangePickerMode}`;
  onAnchorClose?: () => void;
  onDateSelect: DateRangePickerDateSelectFunction;
  showShortcuts?: boolean;
  startDate: NullableDate;
  useTodayFormat?: boolean;
  variant?: `${DateRangePickerVariant}`;
};

export default function DateRangePicker({
  allowAllDates,
  customShortcuts,
  endDate,
  mode = 'text',
  onAnchorClose,
  onDateSelect,
  showShortcuts,
  startDate,
  useTodayFormat,
  variant = 'date_range',
}: DateRangePickerProps) {
  const isDateRangeVariant = variant === 'date_range';
  const isIconMode = mode === 'icon';

  throwIfCustomShortcutsKeysAreNotSafe(
    isDateRangeVariant ? dateRangeVariantShortcuts : singleDateVariantShortcuts,
    customShortcuts,
  );

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [selectedShortcut, setSelectedShortcut] = useState<string>();
  const isOpen = Boolean(anchor);

  const displayDate = useMemo(
    () =>
      getDateRangePickerDisplayDate(
        startDate,
        endDate,
        isDateRangeVariant,
        useTodayFormat,
      ),
    [startDate, endDate, isDateRangeVariant, useTodayFormat],
  );
  const iconModeDisplayDate = useMemo(
    () =>
      getDateRangePickerIconModeDisplayDate(
        startDate,
        endDate,
        isDateRangeVariant,
        useTodayFormat,
      ),
    [startDate, endDate, isDateRangeVariant, useTodayFormat],
  );

  const availableShortcuts = useMemo(() => {
    const hasShortcuts = customShortcuts ? true : showShortcuts;

    if (!hasShortcuts) {
      return [];
    }

    if (isDateRangeVariant) {
      return dateRangeVariantShortcuts.concat(customShortcuts ?? []);
    }

    return singleDateVariantShortcuts.concat(customShortcuts ?? []);
  }, [isDateRangeVariant, customShortcuts, showShortcuts]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchor(null);
    onAnchorClose?.();
  }, [onAnchorClose]);

  const handleDateRangeChange = useCallback(
    (newValue: DateRange<Dayjs>, selectedShortcut: string | undefined = undefined) => {
      onDateSelect(newValue[0], newValue[1], selectedShortcut);
      setSelectedShortcut(selectedShortcut);
    },
    [onDateSelect],
  );

  const handleClearButtonClick = useCallback(() => {
    handleDateRangeChange([null, null], 'all_dates');
  }, [handleDateRangeChange]);

  const handleDateBeforeButtonClick = useCallback(() => {
    const date = startDate ?? dayjs.tz();
    const dateBefore = date.add(-1, 'days');

    handleDateRangeChange(
      [dateBefore.startOf('day'), dateBefore.endOf('day')],
      'date_before',
    );
  }, [handleDateRangeChange, startDate]);

  const handleDateAfterButtonClick = useCallback(() => {
    const date = startDate ?? dayjs.tz();
    const dateAfter = date.add(1, 'days');

    handleDateRangeChange(
      [dateAfter.startOf('day'), dateAfter.endOf('day')],
      'date_after',
    );
  }, [handleDateRangeChange, startDate]);

  useEffect(() => {
    if (selectedShortcut) {
      handleClose();
    }

    return () => {
      setSelectedShortcut(undefined);
    };
  }, [selectedShortcut, handleClose]);

  return (
    <>
      {isIconMode ? (
        <Box
          data-test-id="date-range-picker-icon-mode"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <StyledIconButton onClick={handleDateBeforeButtonClick}>
            <ArrowBackOutlined />
          </StyledIconButton>

          <StyledIconButton onClick={handleOpen}>
            <CalendarMonthOutlined />
          </StyledIconButton>

          <StyledIconButton onClick={handleDateAfterButtonClick}>
            <ArrowForwardOutlined />
          </StyledIconButton>

          <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
            {iconModeDisplayDate}
          </Typography>
        </Box>
      ) : (
        <StyledSmallButton onClick={handleOpen}>{displayDate}</StyledSmallButton>
      )}

      <Popover
        open={isOpen}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {isDateRangeVariant ? (
          <DateRangeCalendar
            value={[startDate, endDate]}
            onChange={(value) => handleDateRangeChange(value)}
            sx={(theme) => ({
              '& .MuiDateRangePickerDay-day.Mui-selected': {
                bgcolor: theme.brandV2.colors.treadYellow,
                '&:hover': { bgcolor: theme.brandV2.colors.treadYellowDark },
              },
              '& .MuiDateRangePickerDay-day:focus.Mui-selected': {
                bgcolor: theme.brandV2.colors.treadYellow,
              },
            })}
          />
        ) : (
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={startDate}
            onChange={(value: Dayjs | null) => handleDateRangeChange([value, null])}
            views={['day']}
            slotProps={{ actionBar: desktopActionBar([]) }}
            sx={(theme) => ({
              '& .MuiPickersDay-root.Mui-selected': {
                bgcolor: theme.brandV2.colors.treadYellow,
                '&:hover': { bgcolor: theme.brandV2.colors.treadYellowDark },
              },
              '& .MuiPickersDay-root:hover:not(.Mui-selected)': {
                border: `solid 1px ${theme.semantic.borderStrong}`,
              },
              '& .MuiPickersCalendarHeader-root': {
                justifyContent: 'center',
                position: 'relative',
              },
              '& .MuiPickersCalendarHeader-labelContainer': {
                mr: 0,
                fontSize: '14px',
              },
              '& .MuiPickersArrowSwitcher-root .MuiPickersArrowSwitcher-button': {
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                '&:first-of-type': { left: '12px' },
                '&:last-child': { right: '12px' },
              },
              '& .MuiPickersArrowSwitcher-spacer': { display: 'none' },
            })}
          />
        )}

        <Box
          sx={(theme) => ({
            alignItems: 'center',
            borderTop: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            gap: 1,
            p: 1,
          })}
        >
          {availableShortcuts.map((shortcut) => (
            <StyledSmallButton
              key={shortcut.label}
              onClick={() => {
                handleDateRangeChange(shortcut.getValue(), shortcut.key);
              }}
            >
              {shortcut.label}
            </StyledSmallButton>
          ))}

          {allowAllDates && (
            <StyledSmallButton onClick={handleClearButtonClick}>
              {t('dispatch.dispatch_v2.filters.all_dates')}
            </StyledSmallButton>
          )}

          <Box sx={{ ml: 'auto' }}>
            <SmallButton color="brandV2OrangeDark" onClick={handleClose} variant="text">
              {t('actions.confirm')}
            </SmallButton>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

const StyledSmallButton = styled(SmallButton)<ButtonProps>(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.semantic.surfaceRaised,
  color: theme.palette.text.primary,
  border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  borderRadius: theme.brandV2.borderRadius,
  boxShadow: 'none',
  display: 'flex',
  height: '30px',
  padidng: theme.spacing(),
  '&:hover': { backgroundColor: theme.semantic.surfaceRaised },
}));

const StyledIconButton = styled(IconButton)<IconButtonProps>(({ disabled, theme }) => ({
  [`&.${iconButtonClasses.root}`]: {
    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
    borderRadius: theme.spacing(0.5),
    color: disabled ? theme.brandV2.colors.treadGray5 : theme.brandV2.colors.treadBlack,
    cursor: disabled ? 'not-allowed' : 'pointer',
    height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
    p: '2px',
    width: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
    [`.${svgIconClasses.root}`]: { fontSize: '18px' },
  },
}));
